import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./AddFamilyMemberForm.scss";
import {
  BENEFICIARY,
  BROTHER,
  DAUGHTER,
  EXTENDED_FAMILY,
  FATHER,
  FEMALE,
  FIANCE,
  GRANDDAUGHTER,
  GRANDPARENT,
  GRANDSON,
  MALE,
  MOTHER,
  OTHER,
  SISTER,
  SON,
  SPOUSE,
  NON_BINARY,
  GENDER_OTHER,
  PREFER_NOT_TO_SAY,
} from "../../Utilities/constants";
import {
  isEmailValid,
  validateMember,
  isPhoneNumberValid,
  validateClientAndCoClient,
} from "../../Utilities/validation";
import {
  formatBEDateStringToFE,
  formatFEDateStringToBE,
  formatTimestampToDate,
} from "../../Utilities/formatter";
import FormControl from "@mui/material/FormControl";
import states_list from "../../Data/us_states.json";
import country_list from "../../Data/country_list.json";
import ClientStatus from "../../Data/client_status.json";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextBox from "../../UX/TextBox/TextBox";
import Button from "../../UX/Button/Button";
import DropDownList from "../../UX/DropDownList/DropDownList";
import MaskedTextBox from "../../UX/MaskedTextBox/MaskedTextBox";
import Switch from "@mui/material/Switch";
import NumericalTextBox from "../../UX/NumericalTextBox/NumericalTextBox";
import NativeDatePicker from "../../UX/NativeDatePicker/NativeDatePicker";
import {Paper} from "@mui/material";
import {styled} from "@mui/material/styles";

const ImprovedSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
          theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const AddFamilyMemberForm = (props) => {
  let defaultRetirementAge = props.defaultRetirementAge ? props.defaultRetirementAge : 67;
  let defaultLifeExpectancyAge = props.defaultLifeExpectancyAge ? props.defaultLifeExpectancyAge : 95;

  const { t } = useTranslation();

  const RELATIONSHIP_DDL = [
    {
      id: BENEFICIARY,
      name: t("ClientDetails.Profile.Family.Relationship.beneficiary"),
    },
    {
      id: SPOUSE,
      name: t("ClientDetails.Profile.Family.Relationship.spouse_partner"),
    },
    { id: SON, name: t("ClientDetails.Profile.Family.Relationship.son") },
    {
      id: DAUGHTER,
      name: t("ClientDetails.Profile.Family.Relationship.daughter"),
    },
    { id: FATHER, name: t("ClientDetails.Profile.Family.Relationship.father") },
    { id: MOTHER, name: t("ClientDetails.Profile.Family.Relationship.mother") },
    { id: SISTER, name: t("ClientDetails.Profile.Family.Relationship.sister") },
    {
      id: BROTHER,
      name: t("ClientDetails.Profile.Family.Relationship.brother"),
    },
    {
      id: GRANDPARENT,
      name: t("ClientDetails.Profile.Family.Relationship.grandparent"),
    },
    {
      id: EXTENDED_FAMILY,
      name: t("ClientDetails.Profile.Family.Relationship.extended_family"),
    },
    {
      id: GRANDSON,
      name: t("ClientDetails.Profile.Family.Relationship.grandson"),
    },
    {
      id: GRANDDAUGHTER,
      name: t("ClientDetails.Profile.Family.Relationship.grand_daughter"),
    },
    {
      id: FIANCE,
      name: t("ClientDetails.Profile.Family.Relationship.fiancee"),
    },
    { id: OTHER, name: t("ClientDetails.Profile.Family.Relationship.other") },
  ];

  const GENDER_DDL = [
    MALE,
    FEMALE,
    NON_BINARY,
    GENDER_OTHER,
    PREFER_NOT_TO_SAY,
  ];

  useEffect(() => {
    let newState = { ...state };
    newState.member = getMemberTemplate(props.member);
    setState(newState);
  }, []);

  const getMemberTemplate = (member) => {
    if (member == null || member.personId == null)
      return getNewMemberTemplate();

    let templateMember = {
      firstName: {
        value: member ? member.firstName : "",
        isValid: true,
      },
      lastName: {
        value: member ? member.lastName : "",
        isValid: true,
      },
      middleName: {
        value: member ? member.middleName : "",
        isValid: true,
      },
      gender: {
        value: member ? member.gender : MALE,
        isValid: true,
      },
      genderOther: {
        value: member ? member.genderOther : "",
        isValid: true,
      },
      dateOfBirth: {
        value: member ? formatBEDateStringToFE(member.dateOfBirth) : null,
        isValid: true,
      },
      status: {
        value: member && member.status ? member.status : ClientStatus.status[0],
        isValid: true,
      },
      relationshipType: member.relationshipType,
      notes: member.notes,
      company:
        member && member.company
          ? member.company
          : {
              companyId: null,
              name: "",
              website: "",
            },
      emailList:
        member && member.emailList
          ? member.emailList
          : [
              {
                emailAddressId: null,
                emailAddress: null,
                emailAddressType: "HOME",
              },
            ],
      phoneList:
        member && member.phoneList
          ? member.phoneList
          : [
              {
                phoneNumberId: null,
                phoneNumber: "",
                phoneNumberType: "PERSONAL",
              },
            ],
      addressList:
        member && member.addressList
          ? member.addressList
          : [
              {
                addressId: null,
                addressLine: null,
                city: null,
                state: {
                  name: "",
                  "alpha-2": "",
                },
                country: {
                  id: "USA",
                  name: "United States",
                },
                zipCode: null,
              },
            ],
    };

    if (member.relationshipType.id === "CLIENT") {
      templateMember.retirementAge = {
        value: member ? member.retirementAge : defaultRetirementAge,
        isValid: true,
      };
      templateMember.lifeExpectancy = {
        value: member ? member.lifeExpectancyAge : defaultLifeExpectancyAge,
        isValid: true,
      };
      templateMember.hasHealthInsurance = {
        value: member.hasHealthInsurance ? member.hasHealthInsurance : false,
        isValid: true,
      };
      templateMember.hasDisabilityInsurance = {
        value: member.hasDisabilityInsurance
          ? member.hasDisabilityInsurance
          : false,
        isValid: true,
      };
      templateMember.hasWill = {
        value: member.hasWill ? member.hasWill : false,
        isValid: true,
      };
      templateMember.hasTrust = {
        value: member.hasTrust ? member.hasTrust : false,
        isValid: true,
      };
      templateMember.isBeneficiariesUpToDate = {
        value: member.isBeneficiariesUpToDate
          ? member.isBeneficiariesUpToDate
          : false,
        isValid: true,
      };
    } else if (member.relationshipType.id === "COCLIENT") {
      templateMember.isEligibleSocialSecuritySpousalBenefit =
        member.isEligibleSocialSecuritySpousalBenefit;
      templateMember.retirementAge = {
        value: member ? member.retirementAge : defaultRetirementAge,
        isValid: true,
      };
      templateMember.lifeExpectancy = {
        value: member ? member.lifeExpectancyAge : defaultLifeExpectancyAge,
        isValid: true,
      };
    }

    return templateMember;
  };

  const getNewMemberTemplate = () => {
    return {
      firstName: {
        value: "",
        isValid: true,
      },
      lastName: {
        value: "",
        isValid: true,
      },
      middleName: {
        value: "",
        isValid: true,
      },
      gender: {
        value: MALE,
        isValid: true,
      },
      genderOther: {
        value: "",
        isValid: true,
      },
      dateOfBirth: {
        value: null,
        isValid: true,
      },
      status: {
        value: ClientStatus.status[0],
        isValid: true,
      },
      relationshipType: {
        id: BENEFICIARY,
        name: t("ClientDetails.Profile.Family.Relationship.beneficiary"),
      },
      notes: null,
      company: {
        companyId: null,
        name: "",
        website: "",
      },
      emailList: [
        {
          emailAddressId: null,
          emailAddress: null,
          emailAddressType: "HOME",
        },
      ],
      phoneList: [
        {
          phoneNumberId: null,
          phoneNumber: "",
          phoneNumberType: "PERSONAL",
        },
      ],
      addressList: [
        {
          addressId: null,
          addressLine: null,
          city: null,
          state: {
            name: "",
            "alpha-2": "",
          },
          country: {
            id: "USA",
            name: "United States",
          },
          zipCode: null,
        },
      ],
      isNew: true,
    };
  };

  const [state, setState] = useState({
    member: getMemberTemplate(props.member),
  });

  let openedDropdownListRef = null;
  const setOpenedDropdownList = (ref) => {
    openedDropdownListRef = ref;
  };

  const hideDropdownOptions = (event) => {
    if (
      event.target.offsetHeight > 250 &&
      openedDropdownListRef &&
      openedDropdownListRef.state.opened
    ) {
      openedDropdownListRef.setState({
        focused: false,
        opened: false,
      });
    }
  };

  const update = (primaryKey, subKey, value) => {
    let newState = { ...state };
    newState[primaryKey].isNew = false;
    newState[primaryKey][subKey].value = value;
    newState[primaryKey][subKey].isValid = true;
    setState(newState);
  };

  const updateValue = (primaryKey, subKey, value) => {
    let newState = { ...state };
    newState[primaryKey].isNew = false;
    newState[primaryKey][subKey] = value;
    setState(newState);
  };

  const updateIsEligibleSocialSecuritySpousalBenefit = (
    primaryKey,
    subKey,
    value
  ) => {
    let newState = { ...state };
    newState[primaryKey].isNew = false;
    newState[primaryKey][subKey] = value;
    if (value) {
      update(primaryKey, "retirementAge", 62);
    }
    setState(newState);
  };

  const updateContact = (index, primaryKey, subKey, value) => {
    let newState = { ...state };
    newState["member"].isNew = false;
    newState["member"][primaryKey][index][subKey] = value;
    if (subKey === "country") {
      if (value.id === "USA") {
        newState["member"]["addressList"][index]["state"] = {
          name: "",
          "alpha-2": "",
        };
      } else {
        newState["member"]["addressList"][index]["state"] = "";
      }
    }
    setState(newState);
  };

  const updateDateOfBirth = (primaryKey, subKey) => {
    return (date) => {
      // make sure we are updating with a valid date and not the keyboard event.
      if (date && date.target) {
        return;
      }

      let newState = { ...state };
      newState[primaryKey].isNew = false;
      newState[primaryKey][subKey].value = date === "" ? null : date;
      newState[primaryKey][subKey].isValid = true;
      setState(newState);
    };
  };

  const updateCompany = (key, value) => {
    let newState = { ...state };
    newState["member"].isNew = false;
    newState["member"]["company"][key] = value;
    setState(newState);
  };

  const save = () => {
    if (validate()) {
      const memberData = generateNewMemberParams();
      props.save(memberData);
    }
  };

  const validate = () => {
    let newState = { ...state };
    newState["member"].isNew = false;
    newState["member"] =
      state.member.relationshipType.id === "CLIENT" ||
      state.member.relationshipType.id === "COCLIENT"
        ? validateClientAndCoClient(newState["member"])
        : validateMember(newState["member"]);

    let otherComponentValid = true;
    if (
      state.member.relationshipType.id === "CLIENT" ||
      state.member.relationshipType.id === "COCLIENT"
    ) {
      newState.member.emailList.forEach((item) => {
        if (!isEmailValid(item.emailAddress)) otherComponentValid = false;
      });
      newState.member.phoneList.forEach((item) => {
        if (props.phoneNumberWithoutValidate) return true
        /*if (!isPhoneNumberValid(item.phoneNumber, true))
          otherComponentValid = false;*/
      });
      newState.member.addressList.forEach((item) => {
        if (item.country.id === "USA") {
          if (item.state["alpha-2"] === "") otherComponentValid = false;
        } else {
          if (item.state === "") otherComponentValid = false;
        }
      });
    }

    let isValid;
    if (
      state.member.relationshipType.id === "CLIENT" ||
      state.member.relationshipType.id === "COCLIENT"
    ) {
      isValid =
        newState["member"]["firstName"].isValid &&
        newState["member"]["lastName"].isValid &&
        newState["member"]["dateOfBirth"].isValid &&
        newState["member"]["retirementAge"].isValid &&
        otherComponentValid;
    } else {
      isValid =
        newState["member"]["firstName"].isValid &&
        newState["member"]["lastName"].isValid &&
        newState["member"]["dateOfBirth"].isValid &&
        otherComponentValid;
    }
    setState(newState);

    return isValid;
  };

  const generateNewMemberParams = () => {
    if (props.member != null && props.member.personId != null) {
      let member = { ...props.member };
      member.personId = props.member.personId;
      member.firstName = state.member.firstName.value;
      member.middleName = state.member.middleName.value;
      member.lastName = state.member.lastName.value;
      member.dateOfBirth = state.member.dateOfBirth.value
        ? formatFEDateStringToBE(state.member.dateOfBirth.value)
        : null;
      member.gender = state.member.gender.value;
      member.genderOther = state.member.genderOther.value;
      member.relationshipType = state.member.relationshipType;
      member.notes = state.member.notes;
      member.company = {
        companyId: state.member.company.companyId,
        name: state.member.company.name,
        website: state.member.company.website,
      };
      member.addressList = state.member.addressList.map((address) => {
        return {
          addressId: address.addressId,
          country: address.country.id,
          addressLine: address.addressLine,
          city: address.city,
          zipCode: address.zipCode,
          state:
            address.country.id === "USA"
              ? address.state["alpha-2"]
              : address.state,
        };
      });
      member.emailList = state.member.emailList.map((email) => {
        return {
          emailAddressId: email.emailAddressId,
          emailAddressType: "HOME",
          emailAddress: email.emailAddress,
        };
      });
      member.phoneList = state.member.phoneList.map((phone) => {
        return {
          phoneNumberId: phone.phoneNumberId,
          phoneNumberType: "PERSONAL",
          phoneNumber: phone.phoneNumber,
        };
      });
      if (state.member.relationshipType.id === "CLIENT") {
        member.status = state.member.status.value;
        member.retirementAge = state.member.retirementAge.value;
        member.lifeExpectancyAge = state.member.lifeExpectancy.value;
        member.hasHealthInsurance = state.member.hasHealthInsurance
          ? state.member.hasHealthInsurance.value
          : false;
        member.hasDisabilityInsurance = state.member.hasDisabilityInsurance
          ? state.member.hasDisabilityInsurance.value
          : false;
        member.hasWill = state.member.hasWill
          ? state.member.hasWill.value
          : false;
        member.hasTrust = state.member.hasTrust
          ? state.member.hasTrust.value
          : false;
        member.isBeneficiariesUpToDate = state.member.isBeneficiariesUpToDate
          ? state.member.isBeneficiariesUpToDate.value
          : false;
      } else if (state.member.relationshipType.id === "COCLIENT") {
        member.isEligibleSocialSecuritySpousalBenefit =
          state.member.isEligibleSocialSecuritySpousalBenefit;
        member.retirementAge = state.member.retirementAge.value;
        member.lifeExpectancyAge = state.member.lifeExpectancy.value;
      }
      return member;
    } else {
      return {
        personId: null,
        firstName: state.member.firstName.value,
        middleName: state.member.middleName.value,
        lastName: state.member.lastName.value,
        salutation: null,
        dateOfBirth: state.member.dateOfBirth.value
          ? formatFEDateStringToBE(state.member.dateOfBirth.value)
          : null,
        gender: state.member.gender.value,
        genderOther: state.member.genderOther.value,
        company: {
          companyId: null,
          name: state.member.company.name,
          website: state.member.company.website,
        },
        relationshipType: state.member.relationshipType,
        notes: state.member.notes,
        addressList: state.member.addressList.map((address) => {
          return {
            addressId: address.addressId,
            country: address.country.id,
            addressLine: address.addressLine,
            city: address.city,
            zipCode: address.zipCode,
            state:
              address.country.id === "USA"
                ? address.state["alpha-2"]
                : address.state,
          };
        }),
        emailList: state.member.emailList.map((email) => {
          return {
            emailAddressId: email.emailAddressId,
            emailAddressType: "HOME",
            emailAddress: email.emailAddress,
          };
        }),
        phoneList: state.member.phoneList.map((phone) => {
          return {
            phoneNumberId: phone.phoneNumberId,
            phoneNumberType: "PERSONAL",
            phoneNumber: phone.phoneNumber,
          };
        }),
      };
    }
  };

  const setupStateComponent = (address, index) => {
    let clientStateField;
    if (address.country.id === "USA") {
      clientStateField = (
        <FormControl
          margin="normal"
          required
          className="percent-95-width state-dropdown"
        >
          <Typography component={"label"}>State</Typography>
          <DropDownList
            placeholder={t("Global.state")}
            value={address.state}
            onChange={(event) =>
              updateContact(index, "addressList", "state", event.target.value)
            }
            data={states_list}
            dataItemKey={"alpha-2"}
            textField={"name"}
            valid={
              address.state["alpha-2"] !== "" ||
              state.member.isNew ||
              (state.member.relationshipType.id !== "CLIENT" &&
                state.member.relationshipType.id !== "COCLIENT")
            }
            reference={setOpenedDropdownList}
          />
        </FormControl>
      );
    } else {
      clientStateField = (
        <FormControl
          margin="normal"
          required
          className="percent-95-width state-textbox"
        >
          <TextBox
            placeholder={t("Global.state")}
            minLength={0}
            maxLength={30}
            value={address.state}
            onChange={(event) =>
              updateContact(index, "addressList", "state", event.target.value)
            }
            hasError={
              !(address.state !== "" || state.member.isNew) &&
              (state.member.relationshipType.id === "CLIENT" ||
                state.member.relationshipType.id === "COCLIENT")
            }
          />
        </FormControl>
      );
    }

    return clientStateField;
  };

  const addAddress = () => {
    const newAddress = {
      addressId: null,
      addressLine: null,
      city: null,
      state: {
        name: "",
        "alpha-2": "",
      },
      country: {
        id: "USA",
        name: "United States",
      },
      zipCode: null,
    };
    let newState = { ...state };
    newState["member"]["addressList"].push(newAddress);
    setState(newState);
  };

  const deleteAddress = (index) => {
    let addressList = [...state["member"]["addressList"]];
    addressList.splice(index, 1);
    let newState = { ...state };
    newState["member"]["addressList"] = addressList;
    setState(newState);
  };

  const addEmail = () => {
    const newEmail = {
      emailAddressId: null,
      emailAddress: null,
      emailAddressType: "HOME",
    };
    let newState = { ...state };
    newState["member"]["emailList"].push(newEmail);
    setState(newState);
  };

  const deleteEmail = (index) => {
    let emailList = [...state["member"]["emailList"]];
    emailList.splice(index, 1);
    let newState = { ...state };
    newState["member"]["emailList"] = emailList;
    setState(newState);
  };

  const addPhone = () => {
    const newPhone = {
      phoneNumberId: null,
      phoneNumber: "",
      phoneNumberType: "PERSONAL",
    };
    let newState = { ...state };
    newState["member"]["phoneList"].push(newPhone);
    setState(newState);
  };

  const deletePhone = (index) => {
    let phoneList = [...state["member"]["phoneList"]];
    phoneList.splice(index, 1);
    let newState = { ...state };
    newState["member"]["phoneList"] = phoneList;
    setState(newState);
  };

  const setupAddressListComponent = (type) => {
    const addressListComponent = state[type].addressList.map(
      (address, index) => {
        let divider;
        if (index > 0) {
          divider = <Grid item xs={12} className="address-divider" />;
        }
        return (
          <React.Fragment>
            {divider}
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <TextBox
                  placeholder={t("Global.address")}
                  minLength={0}
                  maxLength={30}
                  value={address.addressLine}
                  onChange={(event) =>
                    updateContact(
                      index,
                      "addressList",
                      "addressLine",
                      event.target.value
                    )
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <TextBox
                  placeholder={t("Global.city")}
                  minLength={0}
                  maxLength={30}
                  value={address.city}
                  onChange={(event) =>
                    updateContact(
                      index,
                      "addressList",
                      "city",
                      event.target.value
                    )
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              {setupStateComponent(address, index)}
            </Grid>
            <Grid item xs={4}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <Typography component={"label"}>Zip Code</Typography>
                <TextBox
                  placeholder={t("Global.zip_code")}
                  minLength={0}
                  maxLength={10}
                  value={address.zipCode}
                  onChange={(event) =>
                    updateContact(
                      index,
                      "addressList",
                      "zipCode",
                      event.target.value
                    )
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <Typography component={"label"}>Country</Typography>
                <DropDownList
                  placeholder={t("Global.country")}
                  value={address.country}
                  onChange={(event) =>
                    updateContact(
                      index,
                      "addressList",
                      "country",
                      event.target.value
                    )
                  }
                  data={country_list}
                  dataItemKey={"id"}
                  textField={"name"}
                  reference={setOpenedDropdownList}
                />
              </FormControl>
            </Grid>
          </React.Fragment>
        );
      }
    );
    return addressListComponent;
  };

  const setupEmailListComponent = (type) => {
    const emailListComponent = state[type].emailList.map((email, index) => {
      return (
        <React.Fragment>
          <Grid item xs={6}>
            <FormControl margin="normal" required className="percent-95-width">
              <TextBox
                placeholder={t("Global.email")}
                minLength={0}
                maxLength={50}
                value={email.emailAddress}
                onChange={(event) =>
                  updateContact(
                    index,
                    "emailList",
                    "emailAddress",
                    event.target.value
                  )
                }
                validate={(value) => {
                  if (!(isEmailValid(value) || value === ""))
                    return "Email is not valid";
                }}
                hasError={
                  !(isEmailValid(email.emailAddress) || state.member.isNew) &&
                  (state.member.relationshipType.id === "CLIENT" ||
                    state.member.relationshipType.id === "COCLIENT")
                }
              />
            </FormControl>
          </Grid>
        </React.Fragment>
      );
    });
    return emailListComponent;
  };

  const setupPhoneListComponent = (type) => {
    const phoneListComponent = state[type].phoneList.map((phone, index) => {
      return (
        <React.Fragment>
          <Grid item xs={6}>
            <FormControl margin="normal" required className="percent-95-width">
              <MaskedTextBox
                placeholder={t("Global.phone")}
                mask={"999-000-0000"}
                value={phone.phoneNumber}
                onChange={(event) =>
                  updateContact(
                    index,
                    "phoneList",
                    "phoneNumber",
                    event.target.value
                  )
                }
              />
            </FormControl>
          </Grid>
        </React.Fragment>
      );
    });
    return phoneListComponent;
  };

  const setupContactInfoComponent = (type) => {
    const contactComponents = (
      <React.Fragment>
        <Grid item xs={12} style={{ marginTop: "20px" }}>
          <Typography
            component={"p"}
            variant={"body1"}
            color={"primary"}
            style={{ cursor: "pointer" }}
            className={"client-form-section-header"}
          >
            {t("Clients.contact_info_optional_fields")}
          </Typography>
        </Grid>
        {setupEmailListComponent("member")}
        {setupPhoneListComponent("member")}
        {setupAddressListComponent("member")}
      </React.Fragment>
    );

    return contactComponents;
  };

  let modalTitle;
  if (props.member == null) {
    modalTitle = t("ClientDetails.Profile.Family.addFamily");
  } else if (props.member.relationshipType.id === "CLIENT") {
    modalTitle = t("Clients.edit_client");
  } else if (props.member.relationshipType.id === "COCLIENT") {
    modalTitle = t("Clients.edit_coclient");
  } else {
    modalTitle = t("ClientDetails.Profile.Family.editFamily");
  }

  let buttonsGroup = (
    <React.Fragment>
      <span style={{ marginLeft: "10px" }}>
        <Button label={props.saveLabel ? props.saveLabel : "Next"} primary={true} outline={true} onClick={save} />
      </span>
    </React.Fragment>
  );

  let clientFirstNameComponent;
  if (props.member) {
    clientFirstNameComponent = (
      <TextBox
        placeholder={t("Clients.first_name")}
        minLength={0}
        maxLength={30}
        value={state.member.firstName.value}
        onChange={(event) => update("member", "firstName", event.target.value)}
        validate={(value) => {
          if (value === "") return "First Name can't be empty.";
        }}
        hasError={!state.member.firstName.isValid}
        autoFocus
      />
    );
  } else {
    clientFirstNameComponent = (
      <TextBox
        placeholder={t("Clients.first_name")}
        minLength={0}
        maxLength={30}
        value={state.member.firstName.value}
        onChange={(event) => update("member", "firstName", event.target.value)}
        validate={(value) => {
          if (value === "") return "First Name can't be empty.";
        }}
        hasError={!state.member.firstName.isValid}
      />
    );
  }

  let questionAnswerContent;
  if (state.member.relationshipType.id === "CLIENT") {
    questionAnswerContent = (
      <React.Fragment>
        <Grid item xs={12} style={{ marginTop: "20px" }}>
          <Typography
            component={"p"}
            variant={"body1"}
            color={"primary"}
            style={{ cursor: "pointer" }}
            className={"client-form-section-header"}
          >
            {t("Clients.profile_questions")}
          </Typography>
        </Grid>
        <Grid item xs={3} className="question">
          <FormControl margin="normal" required className="percent-95-width">
            <label>
              {t("Clients.add_new_client.health_insurance_question")}
            </label>
          </FormControl>
        </Grid>
        <Grid item xs={1} className="answer">
          <FormControl margin="normal" required className="percent-95-width">
            <ImprovedSwitch
              color={"primary"}
              checked={state.member.hasHealthInsurance.value}
              onChange={(event) =>
                update(
                  "member",
                  "hasHealthInsurance",
                  !state.member.hasHealthInsurance.value
                )
              }
              onLabel={""}
              offLabel={""}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3} className="question">
          <FormControl margin="normal" required className="percent-95-width">
            <label>
              {t("Clients.add_new_client.disability_insurance_question")}
            </label>
          </FormControl>
        </Grid>
        <Grid item xs={1} className="answer">
          <FormControl margin="normal" required className="percent-95-width">
            <ImprovedSwitch
              color={"primary"}
              checked={state.member.hasDisabilityInsurance.value}
              onChange={(event) =>
                update(
                  "member",
                  "hasDisabilityInsurance",
                  !state.member.hasDisabilityInsurance.value
                )
              }
              onLabel={""}
              offLabel={""}
            />
          </FormControl>
        </Grid>
        <Grid item xs={2}></Grid>
        <Grid item xs={3} className="question">
          <FormControl margin="normal" required className="percent-95-width">
            <label>{t("Clients.add_new_client.will_question")}</label>
          </FormControl>
        </Grid>
        <Grid item xs={1} className="answer">
          <FormControl margin="normal" required className="percent-95-width">
            <ImprovedSwitch
              color={"primary"}
              checked={state.member.hasWill.value}
              onChange={(event) =>
                update("member", "hasWill", !state.member.hasWill.value)
              }
              onLabel={""}
              offLabel={""}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3} className="question">
          <FormControl margin="normal" required className="percent-95-width">
            <label>{t("Clients.add_new_client.trust_question")}</label>
          </FormControl>
        </Grid>
        <Grid item xs={1} className="answer">
          <FormControl margin="normal" required className="percent-95-width">
            <ImprovedSwitch
              color={"primary"}
              checked={state.member.hasTrust.value}
              onChange={(event) =>
                update("member", "hasTrust", !state.member.hasTrust.value)
              }
              onLabel={""}
              offLabel={""}
            />
          </FormControl>
        </Grid>
        <Grid item xs={2}></Grid>
        <Grid item xs={3} className="question">
          <FormControl margin="normal" required className="percent-95-width">
            <label>
              {t("Clients.add_new_client.beneficiaries_up_to_date_question")}
            </label>
          </FormControl>
        </Grid>
        <Grid item xs={1} className="answer">
          <FormControl margin="normal" required className="percent-95-width">
            <ImprovedSwitch
              color={"primary"}
              checked={state.member.isBeneficiariesUpToDate.value}
              onChange={(event) =>
                update(
                  "member",
                  "isBeneficiariesUpToDate",
                  !state.member.isBeneficiariesUpToDate.value
                )
              }
              onLabel={""}
              offLabel={""}
            />
          </FormControl>
        </Grid>
        <Grid item xs={7}></Grid>
      </React.Fragment>
    );
  }

  let retirementAgeLifeExpectancyComponent;
  if (
    state.member.relationshipType.id === "CLIENT" ||
    state.member.relationshipType.id === "COCLIENT"
  ) {
    retirementAgeLifeExpectancyComponent = (
      <React.Fragment>
        <Grid item xs={4}>
          <FormControl margin="normal" required className="percent-95-width">
            <Typography component={"label"}>Retirement Age</Typography>
            <NumericalTextBox
              placeholder={t("Clients.retirement_age")}
              value={state.member.retirementAge.value}
              min={1}
              max={200}
              step={1}
              format="#"
              hideArrows={true}
              valid={state.member.retirementAge.isValid}
              update={(event) =>
                update("member", "retirementAge", event.target.value)
              }
              defaultValue={props.defaultRetirementAge}
              disabled={
                state.member.relationshipType.id === "COCLIENT"
                  ? state.member.isEligibleSocialSecuritySpousalBenefit
                  : false
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl margin="normal" required className="percent-95-width">
            <Typography component={"label"}>Life Expectancy</Typography>
            <NumericalTextBox
              placeholder={t("Clients.life_expectancy")}
              value={state.member.lifeExpectancy.value}
              min={1}
              max={200}
              step={1}
              format="#"
              valid={state.member.lifeExpectancy.isValid}
              hideArrows={true}
              defaultValue={props.defaultLifeExpectancyAge}
              update={(event) =>
                update("member", "lifeExpectancy", event.target.value)
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={4}></Grid>
      </React.Fragment>
    );
  }

  let relationshipOrStatusComponent;
  if (state.member.relationshipType.id === "CLIENT") {
    relationshipOrStatusComponent = (
      <Grid item xs={4}>
        <FormControl margin="normal" required className="percent-95-width">
          <Typography component={"label"}>Status</Typography>
          <DropDownList
            placeholder={t("Clients.status")}
            data={ClientStatus.status}
            textField={"value"}
            dataItemKey={"id"}
            value={state.member.status.value}
            onChange={(event) => update("member", "status", event.target.value)}
            reference={setOpenedDropdownList}
          />
        </FormControl>
      </Grid>
    );
  } else {
    relationshipOrStatusComponent = (
      <Grid item xs={4}>
        <FormControl margin="normal" required className="percent-95-width">
          <Typography component={"label"}>Relationship</Typography>
          <DropDownList
            placeholder={t("ClientDetails.Profile.Family.relationship")}
            data={RELATIONSHIP_DDL}
            textField={"name"}
            dataItemKey={"id"}
            value={state.member.relationshipType}
            disabled={
              state.member.relationshipType.id === "CLIENT" ||
              state.member.relationshipType.id === "COCLIENT"
                ? true
                : false
            }
            onChange={(event) =>
              updateValue("member", "relationshipType", event.target.value)
            }
            reference={setOpenedDropdownList}
          />
        </FormControl>
      </Grid>
    );
  }

  let content = (
    <React.Fragment>
      <Grid container spacing={3}>
        <Grid item xs={8}></Grid>
        <Grid
          item
          xs={4}
          className="buttons-group"
          style={{ textAlign: "right" }}
        >
          <div className={"client-wizard-buttons"}>{buttonsGroup}</div>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={3}
        className="add-family-member-modal-div"
        onScroll={hideDropdownOptions}
      >
        <Grid item xs={12}>
          <Grid container spacing={3} >
            <Grid className={"client-section-container"} item xs={12}>
              <Paper elevation={2} className={"client-section"}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography component={"p"} variant={"body1"} color={"primary"} className={"client-form-section-header"}>
                      {t("Clients.basic_information")}
                      <span className="required-condition">
                  {t("Clients.basic_information_required")}
                </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      {clientFirstNameComponent}
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <TextBox
                          placeholder={t("Clients.middle_name")}
                          minLength={0}
                          maxLength={30}
                          value={state.member.middleName.value}
                          onChange={(event) =>
                              update("member", "middleName", event.target.value)
                          }
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <TextBox
                          placeholder={t("Clients.last_name")}
                          minLength={0}
                          maxLength={30}
                          value={state.member.lastName.value}
                          onChange={(event) =>
                              update("member", "lastName", event.target.value)
                          }
                          validate={(value) => {
                            if (value === "") return "Last Name can't be empty.";
                          }}
                          hasError={!state.member.lastName.isValid}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <Typography component={"label"}>Gender</Typography>
                      <DropDownList
                          placeholder={t("Clients.gender")}
                          data={GENDER_DDL}
                          value={state.member.gender.value}
                          onChange={(event) =>
                              update("member", "gender", event.target.value)
                          }
                          reference={setOpenedDropdownList}
                      />
                    </FormControl>
                  </Grid>
                  {relationshipOrStatusComponent}
                  <Grid item xs={4} className="date-of-birth-div">
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <NativeDatePicker
                          className={
                            state.member.dateOfBirth.value == null &&
                            !state.member.isNew
                                ? "MuiInput-root invalid MuiInput-underline Mui-error invalid"
                                : null
                          }
                          placeholder={t("Clients.date_of_birth")}
                          selectedDate={
                            state.member.dateOfBirth.value
                                ? state.member.dateOfBirth.value
                                : null
                          }
                          maxDate={formatTimestampToDate(
                              new Date().getTime(),
                              "yyyy-MM-dd"
                          )}
                          onSelect={updateDateOfBirth("member", "dateOfBirth")}
                          hasError={
                            !(
                                state.member.dateOfBirth.isValid ||
                                !state.member.dateOfBirth.value
                            )
                          }
                          minDateMessage="Date of Birth cannot be before 1900"
                          maxDateMessage="Date of Birth cannot be after today"
                      />
                      {state.member.dateOfBirth.value == null &&
                          !state.member.isNew && (
                              <span className="invalid">
                      Date of Birth field can’t be empty
                    </span>
                          )}
                    </FormControl>
                  </Grid>

                  {state.member.gender.value === "Other" && (
                      <React.Fragment>
                        <Grid item xs={4}>
                          <FormControl
                              margin="normal"
                              required
                              className="percent-95-width"
                          >
                            <TextBox
                                placeholder={"Other Gender"}
                                minLength={0}
                                maxLength={30}
                                value={state.member.genderOther.value}
                                onChange={(event) =>
                                    update("member", "genderOther", event.target.value)
                                }
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={8}></Grid>
                      </React.Fragment>
                  )}
                  {retirementAgeLifeExpectancyComponent}
                </Grid>
              </Paper>
            </Grid>
            <Grid className={"client-section-container"} item xs={12}>
              <Paper elevation={2} className={"client-section"}>
                <Grid container spacing={3}>
                  {setupContactInfoComponent("member")}
                </Grid>
              </Paper>
            </Grid>
            {questionAnswerContent && <Grid className={"client-section-container"} item xs={12}>
              <Paper elevation={2} className={"client-section"}>
                <Grid container spacing={3}>
                  {questionAnswerContent}
                </Grid>
              </Paper>
            </Grid>}
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );

  return (
    <div className="add-family-member-modal-container">
      <Typography component={"h1"} variant={"h1"}>
        {modalTitle}
      </Typography>
      {content}
    </div>
  );
};

export default AddFamilyMemberForm;
